import { buildChatTranscriptEntries } from "../ai-chat/chatTranscript.ts";
import { displayedSpokenAnswer } from "../audio/speechDeliveryTags.ts";
import type { ShellChatTurn } from "../../host/index.ts";

export type TranscriptPolicy = "auto" | "always" | "manual";
export const TRANSCRIPT_QUIET_MS = 8_000;
/**
 * The sphere's square box: the drawn sphere plus the ring of controls around
 * it. The window is this size while the conversation is closed.
 */
export const SPHERE_SIZE = 240;
/**
 * How large the WebGL sphere draws in its box. In a 240px box this draws the
 * particle shell about 128px across, about 16px inside the controls' ring.
 */
export const SPHERE_VISUAL_SCALE = 0.825;
export type SphereSource = string | { id?: string | number; title?: string; date?: string; url?: string };
/**
 * The last message of a turn carries the turn's sources and confidence.
 * `reportReactId` marks the answer that ran a shared Commons procedure, which the user can report.
 */
export type SphereMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** The `reactId` of the turn an answer belongs to. */
  turnId?: string;
  reportReactId?: string;
  sources?: SphereSource[];
  confidence?: string;
  /**
   * Links a server URL sink refused this turn (`url_not_bound`) that the
   * server bound for this user: shown held, so the user's own tap loads one
   * and lets the next turn use it. Never a URL the model alone named.
   */
  heldLinks?: string[];
};

const MAX_HELD_LINKS = 3;

/**
 * The URLs of a turn's `url_not_bound` refusals that carry a signed binding
 * (`boundUrls`, registered into the bound set by the intake). A refusal
 * without one names no link: its answer text says how to go on.
 */
export function refusedBoundLinks(fetchedData: unknown): string[] {
  if (!Array.isArray(fetchedData)) return [];
  const links: string[] = [];
  for (const item of fetchedData) {
    if (typeof item !== "object" || item === null) continue;
    const { action, code, boundUrls } = item as Record<string, unknown>;
    if (action !== "risk_gate" || code !== "url_not_bound" || !Array.isArray(boundUrls)) continue;
    for (const bound of boundUrls) {
      const url = typeof bound === "object" && bound !== null ? (bound as Record<string, unknown>).url : undefined;
      if (typeof url === "string" && url && !links.includes(url)) links.push(url);
    }
  }
  return links.slice(0, MAX_HELD_LINKS);
}

function ranSharedPlaybook(entry: Record<string, unknown>) {
  const context = entry.contextData;
  return !entry.requestPending && typeof context === "object" && context !== null && Boolean((context as Record<string, unknown>).platformPlaybook);
}

function sphereSources(value: unknown): SphereSource[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((source): SphereSource[] => {
    if (typeof source === "string") return source.trim() ? [source.trim()] : [];
    if (typeof source !== "object" || source === null) return [];
    const { id, title, date, url } = source as Record<string, unknown>;
    return [{
      ...(typeof id === "string" || typeof id === "number" ? { id } : {}),
      ...(typeof title === "string" ? { title } : {}),
      ...(typeof date === "string" ? { date } : {}),
      ...(typeof url === "string" ? { url } : {}),
    }];
  });
}

/** "82% confidence" from a 0–1 score, "high confidence" from a label, or nothing. */
export function formatConfidence(entry: Record<string, unknown>): string | undefined {
  const feasibility = entry.feasibility;
  const raw = entry.confidence ?? (typeof feasibility === "object" && feasibility !== null ? (feasibility as Record<string, unknown>).confidence : undefined);
  if (typeof raw === "number" && Number.isFinite(raw)) return `${Math.round(Math.max(0, Math.min(1, raw)) * 100)}% confidence`;
  if (typeof raw === "string" && raw.trim()) return `${raw.trim().toLowerCase()} confidence`;
  return undefined;
}

export function normalizeTranscriptPolicy(value: unknown): TranscriptPolicy {
  return value === "always" || value === "manual" ? value : "auto";
}

/** Use the existing turn/revision identity rules, never deduplicate by prompt text. */
export function sphereConversationMessages(history: readonly ShellChatTurn[], draft: string, transcribing: boolean): SphereMessage[] {
  const entries = buildChatTranscriptEntries(history.map(chat => ({
    ...chat,
    question: chat.originalQuestion || chat.question,
    textAnswer: displayedSpokenAnswer(chat.audioAnswer, chat.textAnswer, chat.answerCaption),
  }))).reverse();
  const messages: SphereMessage[] = [];
  entries.forEach((entry, index) => {
    const key = String(entry.reactId || `legacy-${index}`);
    const question = String(entry.question || "").trim();
    const shown = question && question !== "GREETING";
    if (shown) messages.push({ id: `${key}:user`, role: "user", text: question });
    const answers = [...entry.answers].reverse();
    const reactId = typeof entry.reactId === "string" ? entry.reactId.trim() : "";
    answers.forEach((text, answer) => messages.push({ id: `${key}:answer:${answer}`, role: "assistant", text, ...(reactId ? { turnId: reactId } : {}) }));
    // A turn with no answer text (an action-only result) carries its sources and report link on the question.
    const last = answers.length || shown ? messages.at(-1) : undefined;
    if (!last) return;
    const sources = sphereSources(entry.sources);
    const confidence = formatConfidence(entry);
    const heldLinks = refusedBoundLinks(entry.fetchedData);
    if (reactId && ranSharedPlaybook(entry)) last.reportReactId = reactId;
    if (sources.length) last.sources = sources;
    if (confidence) last.confidence = confidence;
    if (heldLinks.length) last.heldLinks = heldLinks;
  });
  const text = draft.trim();
  const latestUser = messages.findLast(message => message.role === "user");
  if (text && text !== "GREETING" && (transcribing || latestUser?.text !== text)) {
    messages.push({ id: "live-draft", role: "user", text });
  }
  return messages;
}

export function transcriptIsOpen(policy: TranscriptPolicy, manual: boolean | null, active: boolean, holding: boolean, quietUntil: number, now: number) {
  return manual ?? (policy === "always" || (policy === "auto" && (active || holding || now < quietUntil)));
}

/** Whether the message log hides lines above or below what it shows. */
export type TranscriptScrollEdges = { above: boolean; below: boolean };

/** Slack for sub-pixel rounding: a log this close to its end counts as at the end. */
const SCROLL_END_SLACK_PX = 8;

export function transcriptScrollEdges(node: { scrollTop: number; scrollHeight: number; clientHeight: number }): TranscriptScrollEdges {
  return {
    above: node.scrollTop > SCROLL_END_SLACK_PX,
    below: node.scrollHeight - node.scrollTop - node.clientHeight > SCROLL_END_SLACK_PX,
  };
}

const EDGE_FADE_PX = 20;

/**
 * Fades the log's edge where lines continue, so a line cut by the log's
 * height reads as "scroll for more", not as a clipped answer.
 */
export function transcriptEdgeMask(edges: TranscriptScrollEdges): { maskImage?: string; WebkitMaskImage?: string } {
  if (!edges.above && !edges.below) return {};
  const top = edges.above ? `transparent 0, #000 ${EDGE_FADE_PX}px` : "#000 0";
  const bottom = edges.below ? `#000 calc(100% - ${EDGE_FADE_PX}px), transparent 100%` : "#000 100%";
  const mask = `linear-gradient(to bottom, ${top}, ${bottom})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

/** Transcript column: header row, message log, and the typed composer pinned under it. */
export const TRANSCRIPT_PANEL_HEIGHT = 288;
const STACK_GAP = 16;
const TRANSCRIPT_PANEL_WIDTH = 342;
/** Sphere and transcript side by side. */
const SIDE_LAYOUT_WIDTH = SPHERE_SIZE + STACK_GAP + TRANSCRIPT_PANEL_WIDTH;
const BELOW_LAYOUT_MAX_WIDTH = 370;
const MIN_TRANSCRIPT_ROOM = 250;
/** Space the open transcript keeps clear of the workspace's right edge. */
export const TRANSCRIPT_EDGE_GAP = 16;

/** A closed conversation is the bare sphere: the transcript and composer show only together. */
export function sphereConversationSize(open: boolean, below: boolean, availableWidth: number) {
  return open
    ? { width: below ? Math.max(SPHERE_SIZE, Math.min(BELOW_LAYOUT_MAX_WIDTH, availableWidth)) : SIDE_LAYOUT_WIDTH, height: below ? SPHERE_SIZE + STACK_GAP + TRANSCRIPT_PANEL_HEIGHT : TRANSCRIPT_PANEL_HEIGHT }
    : { width: SPHERE_SIZE, height: SPHERE_SIZE };
}

/** Width the conversation can use from its left edge at `x` to the workspace's right edge. */
export function sphereTranscriptRoom(x: number, workspaceWidth: number) {
  return Math.max(MIN_TRANSCRIPT_ROOM, workspaceWidth - Math.max(0, x) - TRANSCRIPT_EDGE_GAP);
}

/** The transcript moves under the sphere when it can't fit beside it. */
export function transcriptGoesBelow(room: number) {
  return room < SIDE_LAYOUT_WIDTH;
}

/**
 * The most workspace area the conversation window takes on its own: the bare
 * sphere plus the open transcript beside or under it, and the gap the
 * transcript keeps from the right edge. The window grows to its open size
 * whenever the transcript opens, so placement reserves this footprint and no
 * other window opens where the transcript will appear. Without `x`, it is the
 * footprint the window needs anywhere in the workspace: the side layout when
 * the workspace is wide enough for it.
 */
export function sphereConversationFootprint(x: number | undefined, workspaceWidth: number) {
  const room = sphereTranscriptRoom(x ?? 0, workspaceWidth);
  const below = transcriptGoesBelow(room);
  const open = sphereConversationSize(true, below, room);
  return {
    width: Math.max(SPHERE_SIZE, open.width + (below ? 0 : TRANSCRIPT_EDGE_GAP)),
    height: Math.max(SPHERE_SIZE, open.height),
  };
}
